import { useRef } from "react";
import { Easing, Animated as RNAnimated } from "react-native";
import { ScrollState } from "./types";

export type UseAutoScrollArgs = {
  scrollHeight: number;
  speed: number;
};

export type UseAutoScrollReturn = {
  scrollToTop: () => void;
  scrollToBottom: () => void;
  cancelScroll: () => void;
  scrollRef: React.MutableRefObject<any>;
  scrollState: React.MutableRefObject<ScrollState>;
};

/**
 * Scroll to top or bottom of scroll view at chosen speed with cancellable actions
 * @param scrollHeight total height of a scroll view's content
 * @param speed speed of scrolling in ms per px
 */
export function useAutoScroll({
  scrollHeight,
  speed,
}: UseAutoScrollArgs): UseAutoScrollReturn {
  const scrollRef = useRef<any>();
  const scrollAnimation = useRef(new RNAnimated.Value(0));
  const scrollState = useRef<ScrollState>(ScrollState.NotScrolling);

  function animatedScrollTo(offsetY: number) {
    if (scrollState.current !== ScrollState.NotScrolling) {
      cancelScroll();
    }

    //@ts-ignore
    const currentOffset = scrollAnimation.current._value;

    const animation = RNAnimated.timing(scrollAnimation.current, {
      toValue: offsetY,
      duration: Math.abs(offsetY - currentOffset) * speed,
      useNativeDriver: true,
      easing: Easing.linear,
    });

    animation.start(cancelScroll);

    scrollAnimation.current.addListener((animation: any) => {
      scrollRef.current &&
        scrollRef.current.scrollTo({
          y: animation.value,
          animated: false,
        });
    });

    return animation;
  }

  function cancelScroll() {
    scrollAnimation.current?.stopAnimation();
    scrollAnimation.current?.removeAllListeners();
    scrollState.current = ScrollState.NotScrolling;
  }

  function scrollToTop() {
    scrollState.current = ScrollState.ScrollingUp;
    animatedScrollTo(0);
  }

  function scrollToBottom() {
    scrollState.current = ScrollState.ScrollingDown;
    animatedScrollTo(scrollHeight);
  }

  return {
    scrollToTop,
    scrollToBottom,
    cancelScroll,
    scrollRef,
    scrollState,
  };
}
